import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Calendar, Globe, Loader2, Megaphone } from "lucide-react";
import { useEffect, useMemo } from "react";
import { BrandCard, BrandCardContent } from "@/components/brand/brand-card";
import { Header } from "@/components/Header";
import { useSession } from "@/hooks/auth-hooks";
import { AD_STATUS } from "@/lib/ad-pricing";

export const Route = createFileRoute("/admin/ads")({
	component: AdminAdsPage,
});

type AdminAd = {
	id: string;
	title?: string | null;
	description?: string | null;
	url?: string | null;
	status: number;
	amountCents?: number | null;
	orderNo?: string | null;
	customerEmail?: string | null;
	activatedAt?: string | null;
	expiresAt?: string | null;
	createdAt?: string | null;
	user?: Array<{ id: string; email?: string; name?: string }>;
};

type AdminOrder = {
	id: string;
	orderNo: string;
	status: string;
	productName?: string | null;
	amount?: number | null;
	currency?: string | null;
	paymentEmail?: string | null;
	paidAt?: string | null;
	createdAt?: string | null;
};

const STATUS_STYLES: Record<number, string> = {
	[AD_STATUS.ACTIVE]: "bg-blue-500/15 text-blue-400 ring-blue-500/20",
	[AD_STATUS.RESERVED]: "bg-violet-500/15 text-violet-400 ring-violet-500/20",
	[AD_STATUS.AVAILABLE]: "bg-green-500/15 text-green-400 ring-green-500/20",
	[AD_STATUS.EXPIRED]: "bg-red-500/15 text-red-400 ring-red-500/20",
	[AD_STATUS.DELETED]: "bg-stone-500/15 text-stone-400 ring-stone-500/20",
};

function statusLabel(status: number): string {
	switch (status) {
		case AD_STATUS.ACTIVE:
			return "active";
		case AD_STATUS.RESERVED:
			return "reserved";
		case AD_STATUS.AVAILABLE:
			return "available";
		case AD_STATUS.EXPIRED:
			return "expired";
		case AD_STATUS.DELETED:
			return "deleted";
		default:
			return String(status);
	}
}

function AdminAdsPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();

	const isAdmin = user?.role === "admin";

	useEffect(() => {
		if (isPending) return;
		if (!session || !user) {
			navigate({ to: "/login", replace: true });
			return;
		}
		if (!isAdmin) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isPending, session, user, isAdmin, navigate]);

	const { data, isLoading } = useQuery({
		queryKey: ["admin-ads"],
		queryFn: async () => {
			const res = await fetch("/api/admin/list-ads", {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load ads");
			return (await res.json()) as { ads: AdminAd[]; orders: AdminOrder[] };
		},
		enabled: !isPending && !!session && !!user && isAdmin,
	});

	const ads = useMemo(() => data?.ads ?? [], [data]);
	const orders = useMemo(() => data?.orders ?? [], [data]);

	const counts = useMemo(() => {
		const c = { active: 0, reserved: 0, available: 0, expired: 0 };
		for (const ad of ads) {
			if (ad.status === AD_STATUS.ACTIVE) c.active++;
			else if (ad.status === AD_STATUS.RESERVED) c.reserved++;
			else if (ad.status === AD_STATUS.AVAILABLE) c.available++;
			else if (ad.status === AD_STATUS.EXPIRED) c.expired++;
		}
		return c;
	}, [ads]);

	if (isPending || isLoading || !isAdmin) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<Loader2 className="size-8 animate-spin text-foreground" />
			</div>
		);
	}

	return (
		<div className="min-h-svh bg-background">
			<Header />

			<main className="max-w-5xl mx-auto px-4 py-8">
				<div className="mb-8">
					<h1 className="text-2xl font-normal text-foreground tracking-[-0.04em]">
						Ads
					</h1>
					<p className="mt-1 text-base text-muted-foreground">
						{counts.active} active, {counts.reserved} reserved,{" "}
						{counts.available} available, {counts.expired} expired
					</p>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
					{ads.map((ad) => {
						const statusStyle =
							STATUS_STYLES[ad.status] ?? STATUS_STYLES[AD_STATUS.AVAILABLE];
						const linkedUser = ad.user?.[0];
						return (
							<BrandCard key={ad.id}>
								<BrandCardContent className="p-5">
									<div className="flex items-center justify-between mb-3">
										<span className="text-base font-normal text-foreground truncate">
											{ad.title || "Untitled"}
										</span>
										<span
											className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-normal ring-1 ring-inset ${statusStyle}`}
										>
											{statusLabel(ad.status)}
										</span>
									</div>

									{ad.url && (
										<a
											href={ad.url}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
										>
											<Globe className="size-3" />
											{ad.url.replace(/^https?:\/\//, "")}
										</a>
									)}

									{ad.description && (
										<p className="text-sm text-muted-foreground mb-2 line-clamp-2">
											{ad.description}
										</p>
									)}

									{typeof ad.amountCents === "number" && (
										<p className="text-sm text-muted-foreground mb-2">
											<Megaphone className="inline size-3 mr-1" />$
											{(ad.amountCents / 100).toFixed(0)}
										</p>
									)}

									{(linkedUser || ad.customerEmail) && (
										<p className="text-sm text-muted-foreground truncate mb-2">
											{linkedUser?.name ??
												linkedUser?.email ??
												ad.customerEmail}
										</p>
									)}

									{(ad.activatedAt || ad.expiresAt) && (
										<div className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
											{ad.activatedAt && (
												<span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
													<Calendar className="size-3" />
													From{" "}
													{new Date(ad.activatedAt).toLocaleDateString(
														undefined,
														{ month: "short", day: "numeric" },
													)}
												</span>
											)}
											{ad.expiresAt && (
												<span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
													<Calendar className="size-3" />
													Until{" "}
													{new Date(ad.expiresAt).toLocaleDateString(
														undefined,
														{
															month: "short",
															day: "numeric",
														},
													)}
												</span>
											)}
										</div>
									)}
								</BrandCardContent>
							</BrandCard>
						);
					})}
				</div>

				{ads.length === 0 && (
					<BrandCard>
						<BrandCardContent className="py-16">
							<div className="text-center">
								<Megaphone className="size-10 text-muted-foreground/40 mx-auto mb-3" />
								<p className="text-base text-muted-foreground">No ads found</p>
							</div>
						</BrandCardContent>
					</BrandCard>
				)}

				<div className="mt-10 mb-4">
					<h2 className="text-xl font-normal text-foreground tracking-[-0.04em]">
						Recent orders
					</h2>
				</div>

				<div className="space-y-2">
					{orders.slice(0, 50).map((order) => (
						<BrandCard key={order.id}>
							<BrandCardContent className="flex items-center justify-between gap-4 p-4">
								<div className="min-w-0">
									<p className="font-mono text-sm text-foreground truncate">
										{order.orderNo}
									</p>
									<p className="text-sm text-muted-foreground truncate">
										{order.productName ?? "Order"}
										{order.paymentEmail ? ` · ${order.paymentEmail}` : ""}
									</p>
								</div>
								<div className="flex shrink-0 items-center gap-3">
									{typeof order.amount === "number" && (
										<span className="text-sm text-muted-foreground tabular-nums">
											${(order.amount / 100).toFixed(0)}
										</span>
									)}
									<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-normal ring-1 ring-inset bg-muted text-muted-foreground ring-foreground/10">
										{order.status}
									</span>
								</div>
							</BrandCardContent>
						</BrandCard>
					))}
					{orders.length === 0 && (
						<p className="text-sm text-muted-foreground">No orders yet.</p>
					)}
				</div>
			</main>
		</div>
	);
}
